import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Bullet, Bullets, Info, Warning } from '../../components/DocsComponents'

const WorkingWithSubcontractors = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">Assigning subcontractors to shifts</Typography>
      <Typography>
        Wherever you assign staff — event shifts, the Global Roster, PTS
        duties, and CAD resources — the staff selector also searches your
        subcontractors. Subcontractor results are marked with a handshake
        icon and a status chip: green <strong>Active</strong>, amber{' '}
        <strong>Pending</strong>, or red <strong>Inactive</strong>. Selecting
        a subcontractor auto-fills the shift&apos;s clinical role from their
        profile.
      </Typography>
      <Typography>
        Only subcontractors who are Pending Verification or Active with a
        current access window can be assigned. If you pick an inactive one,
        AmbuNet intercepts the selection: members of the{' '}
        <strong>Personnel</strong> team (and site administrators) get a
        one-click <strong>Reactivate</strong> button that grants a fresh
        window using the organisation default and continues the assignment;
        everyone else is asked to speak to their Personnel team.
      </Typography>
      <Info>
        Subcontractors cannot be assigned to <strong>on-call rotas</strong> —
        on-call is for your own employees only.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Cover without a user account
      </Typography>
      <Typography>
        When a partner organisation is sending crew but you don&apos;t know
        (or don&apos;t need) the individual, tick{' '}
        <strong>Covered by subcontractor without a user account</strong> on
        the shift instead of picking a person. The shift shows simply as
        &quot;Subcontractor&quot; in event views, the Global Roster, and PTS
        duty planning, and counts as filled.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">What happens after assignment</Typography>
      <Typography>
        A shift covered by a subcontractor (named or unnamed) is treated as
        filled: it is not advertised on Available Shifts, is excluded from
        shift-availability emails and starting-soon reminders, and no
        acknowledgement is requested.
      </Typography>
      <Warning title="Tell subcontractors about their shifts directly">
        Subcontractors do not see an assigned-shifts list in AmbuNet and do
        not receive shift notifications — always confirm dates, times, and
        locations with them (or their organisation) outside the system.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        What subcontractors can do when they sign in
      </Typography>
      <Typography>
        A subcontractor account gets a deliberately small sidebar: the home
        screen, a <strong>Clinical Tools</strong> section, and help links.
        On shift they can:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>ePCR</strong> — create and complete patient care records.
          Their name and clinical role appear on the record and its printed
          output, and they can be added to any crew&apos;s Staff Involved
          list.
        </Bullet>
        <Bullet>
          <strong>Medicine Administration</strong> — record medicines they
          administer, feeding your controlled-drug and stock tracking as
          normal.
        </Bullet>
        <Bullet>
          <strong>VDIs</strong> — complete vehicle daily inspections for the
          vehicle they are crewing.
        </Bullet>
        <Bullet>
          <strong>Incident Reporting</strong> — report incidents into your
          governance workflow (they cannot browse or manage the incident
          list).
        </Bullet>
        <Bullet>
          <strong>Knowledgebase and Support</strong> — this documentation and
          your support channel.
        </Bullet>
      </Bullets>
      <Typography>
        Everything else is off-limits: no shift self-service or
        applications, no availability or leave, no payroll or My Pay, no
        training or Crew Vault, and no admin or configuration areas. The
        global search is filtered to the same small set of pages.
      </Typography>
      <Info title="Web app only">
        Subcontractors should use AmbuNet in the browser — the native mobile
        app is designed around employee accounts and most of its screens will
        not work for a subcontractor.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Payroll and costing</Typography>
      <Typography>
        Shifts worked by subcontractors appear in{' '}
        <DocLink docsPage url="/employees-hr/rostering-payroll" text="pay run" />
        shift searches — named subcontractors under their own name, unnamed
        cover as &quot;Contractor&quot; — and are included in event cost
        breakdowns, so your shift costs stay complete. Subcontractors have no
        payroll profile (no employment type, contracted hours, or BACS
        details), so pay them through your normal supplier invoicing rather
        than a pay run.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Compliance responsibilities</Typography>
      <Typography>
        AmbuNet does not track training, DBS checks, qualifications, or
        documents for subcontractors — only their clinical role. Regulators
        inspect subcontracted staff as if they were your own, so keep your
        vetting evidence (qualification checks, DBS, indemnity, agreements)
        in your own records or your{' '}
        <DocLink
          docsPage
          url="/clinical-governance/cqc-centre-overview"
          text="CQC evidence"
        />
        process, and use short access windows so accounts never outlive an
        engagement.
      </Typography>
    </Stack>
  </Stack>
)

export default WorkingWithSubcontractors
